import React, {
  ComponentProps,
  FunctionComponent,
  memo,
  useCallback,
  useRef,
} from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { useBodyScrollLock } from '@surglogs-app/hooks'
import { Box, LayerManager, LayerManagerState } from '@surglogs-app/ui'

// background must be scrollable on mobile bcs of safari iOS overflow bug, when content is not visible and not scrollable if parent and child div have set up (max)height

const ModalBackgroundContainer = styled(Box)<{
  offset?: number
  zIndex?: number
}>`
  position: fixed;
  z-index: ${({ zIndex }) => zIndex};
  left: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  overflow: hidden;

  ${({ offset = 0 }) => css`
    top: ${offset}px;
    height: calc(100% - ${offset}px);
    max-height: calc(100% - ${offset}px);
  `}

  display: table;
`

const PositionContainer = styled(Box)`
  width: 100%;
  display: table-cell;
  margin: auto;
  max-height: 100%;
  vertical-align: middle;
`

interface ModalBackgroundProps
  extends ComponentProps<typeof ModalBackgroundContainer> {
  offset?: number
}

const Component: FunctionComponent<ModalBackgroundProps> = memo(
  ({ children, ...rest }) => {
    const ref = useRef<HTMLDivElement>(null)
    useBodyScrollLock(ref, { allowTouchMove: () => true })

    return (
      <ModalBackgroundContainer {...{ ref, px: 'xl', py: 'xxxl', ...rest }}>
        <PositionContainer>{children}</PositionContainer>
      </ModalBackgroundContainer>
    )
  },
)

export const ModalBackground = memo((props: ModalBackgroundProps) => {
  const renderComponent = useCallback(
    ({ zIndex, increaseBy }: LayerManagerState) => (
      <Component {...props} {...{ zIndex: zIndex + increaseBy / 2 + 1 }} />
    ),
    [props],
  )

  return <LayerManager>{renderComponent}</LayerManager>
})
